import React from "react";
import { TaskListSquareLtr16Regular } from "@fluentui/react-icons";
import { mailLinkService, type VorgangsLookupAntwortDto } from "../../services/mailLinkService";
import { abonniereZuweisung } from "./mailAssignmentBus";
import {
    baueVorgangsIndex,
    LEERER_VORGANGS_INDEX,
    vorgangsMarkerLabel,
    vorgangsSchluessel,
    type VorgangsIndex,
    type VorgangsRef,
} from "./mailListMarkers";

type SichtbareMail = {
    conversationId?: string | null;
    srcMailbox?: string;
};

type Lookup = (
    mailbox: string,
    conversationIds: readonly string[],
) => Promise<VorgangsLookupAntwortDto>;

const standardLookup: Lookup = (mailbox, conversationIds) =>
    mailLinkService.lookupVerknuepft(mailbox, conversationIds);

/**
 * P7: hält den Vorgangsindex der sichtbaren MailView-Zeilen aktuell.
 *
 * Der Hook bündelt je Postfach, fragt beim Nachladen nur unbekannte
 * Konversationen ab und lädt nach einer Zuweisungs-Busmeldung genau die
 * betroffene Konversation neu. `lookup` ist nur für den Komponententest
 * injizierbar; produktiv wird immer der tenant-/mailbox-geprüfte Endpoint
 * verwendet.
 */
export function useMailVorgangsIndex({
    emails,
    currentMailbox,
    enabled,
    resetKey,
    lookup = standardLookup,
}: {
    emails: readonly SichtbareMail[];
    currentMailbox: string | null | undefined;
    enabled: boolean;
    resetKey: unknown;
    lookup?: Lookup;
}): VorgangsIndex {
    const [index, setIndex] = React.useState<VorgangsIndex>(LEERER_VORGANGS_INDEX);
    const laufRef = React.useRef(0);
    const bekanntRef = React.useRef<Set<string>>(new Set());
    const generationRef = React.useRef<Map<string, number>>(new Map());
    const sichtbareKeys = React.useMemo(() => {
        const keys = new Set<string>();
        if (!enabled || !currentMailbox) return keys;
        for (const email of emails) {
            const mailbox = email.srcMailbox ?? currentMailbox;
            if (email.conversationId) {
                keys.add(vorgangsSchluessel(mailbox, email.conversationId));
            }
        }
        return keys;
    }, [emails, currentMailbox, enabled]);
    const sichtbareKeysRef = React.useRef<ReadonlySet<string>>(new Set());

    React.useEffect(() => {
        laufRef.current++;
        bekanntRef.current = new Set();
        generationRef.current = new Map();
        setIndex(LEERER_VORGANGS_INDEX);
    }, [currentMailbox, resetKey]);

    React.useEffect(() => {
        sichtbareKeysRef.current = sichtbareKeys;
        for (const key of Array.from(bekanntRef.current)) {
            if (!sichtbareKeys.has(key)) bekanntRef.current.delete(key);
        }
        for (const key of Array.from(generationRef.current.keys())) {
            if (!sichtbareKeys.has(key)) generationRef.current.delete(key);
        }
        setIndex((alt) => {
            let entfernt = false;
            const next = new Map<string, readonly VorgangsRef[]>();
            for (const [key, vorgaenge] of alt) {
                if (sichtbareKeys.has(key)) next.set(key, vorgaenge);
                else entfernt = true;
            }
            return entfernt ? next : alt;
        });
    }, [sichtbareKeys]);

    const laden = React.useCallback(
        (mailbox: string, conversationIds: readonly string[]) => {
            if (conversationIds.length === 0) return;
            const lauf = laufRef.current;
            const generationen = new Map<string, number>();
            for (const id of conversationIds) {
                const key = vorgangsSchluessel(mailbox, id);
                bekanntRef.current.add(key);
                const generation = (generationRef.current.get(key) ?? 0) + 1;
                generationRef.current.set(key, generation);
                generationen.set(key, generation);
            }
            for (let i = 0; i < conversationIds.length; i += 200) {
                const teil = conversationIds.slice(i, i + 200);
                lookup(mailbox, teil)
                    .then((antwort) => {
                        if (laufRef.current !== lauf) return;
                        const aktuell = teil.filter((id) => {
                            const key = vorgangsSchluessel(mailbox, id);
                            return generationRef.current.get(key) === generationen.get(key);
                        });
                        if (aktuell.length > 0) {
                            setIndex((alt) => baueVorgangsIndex(mailbox, aktuell, antwort, alt));
                        }
                    })
                    .catch(() => {
                        // Beiwerk: Die Mailliste bleibt auch ohne Marker nutzbar.
                        // Nur die weiterhin aktuelle Generation freigeben. Hat
                        // inzwischen ein Bus-Reload denselben Schlüssel neu
                        // angefragt, gehört `bekannt` bereits zu diesem neueren
                        // Request und darf nicht zurückgenommen werden.
                        if (laufRef.current !== lauf) return;
                        for (const id of teil) {
                            const key = vorgangsSchluessel(mailbox, id);
                            if (generationRef.current.get(key) === generationen.get(key)) {
                                bekanntRef.current.delete(key);
                            }
                        }
                    });
            }
        },
        [lookup],
    );

    React.useEffect(() => {
        if (!enabled || !currentMailbox) return;
        const offen = new Map<string, string[]>();
        for (const email of emails) {
            const mailbox = email.srcMailbox ?? currentMailbox;
            const conversationId = email.conversationId;
            if (!conversationId || bekanntRef.current.has(vorgangsSchluessel(mailbox, conversationId))) {
                continue;
            }
            const ids = offen.get(mailbox) ?? [];
            if (!ids.includes(conversationId)) ids.push(conversationId);
            offen.set(mailbox, ids);
        }
        for (const [mailbox, ids] of offen) laden(mailbox, ids);
    }, [emails, currentMailbox, enabled, laden]);

    React.useEffect(
        () =>
            abonniereZuweisung((zuweisung) => {
                if (!enabled || zuweisung.vorgang === undefined || !zuweisung.conversationId) return;
                const key = vorgangsSchluessel(zuweisung.mailbox, zuweisung.conversationId);
                if (!sichtbareKeysRef.current.has(key)) return;
                laden(zuweisung.mailbox, [zuweisung.conversationId]);
            }),
        [enabled, laden],
    );

    return index;
}

export function MailVorgangsMarker({
    vorgaenge,
    className,
    onNavigieren,
}: {
    vorgaenge: readonly VorgangsRef[];
    className?: string;
    onNavigieren: (ziel: string) => void;
}) {
    const label = vorgangsMarkerLabel(vorgaenge);
    return (
        <button
            type="button"
            className={className}
            aria-label={label}
            title={label}
            onClick={(event) => {
                event.stopPropagation();
                onNavigieren(`/vorgaenge/${vorgaenge[0].id}`);
            }}
        >
            <TaskListSquareLtr16Regular />
        </button>
    );
}

export function MailVorgangsChips({
    vorgaenge,
    rowClassName,
    chipClassName,
    onNavigieren,
}: {
    vorgaenge: readonly VorgangsRef[];
    rowClassName?: string;
    chipClassName?: string;
    onNavigieren: (ziel: string) => void;
}) {
    if (vorgaenge.length === 0) return null;
    return (
        <div className={rowClassName} title={vorgangsMarkerLabel(vorgaenge)}>
            <TaskListSquareLtr16Regular aria-hidden />
            <span>
                {vorgaenge.length === 1
                    ? "Gehört zum Vorgang:"
                    : `Gehört zu ${vorgaenge.length} Vorgängen:`}
            </span>
            {vorgaenge.map((vorgang) => (
                <button
                    key={vorgang.id}
                    type="button"
                    className={chipClassName}
                    aria-label={`Vorgang öffnen: ${vorgang.titel}`}
                    title={`Vorgang öffnen: ${vorgang.titel}`}
                    onClick={() => onNavigieren(`/vorgaenge/${vorgang.id}`)}
                >
                    {vorgang.titel}
                </button>
            ))}
        </div>
    );
}
